import type { IPktComboboxOption } from 'shared-types/combobox'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import { PktButton, PktCombobox } from '@/components'

import countrycodes from '../../../elements/src/components/combobox/countrycodes.json'

const bydeler: IPktComboboxOption[] = [
  { value: 'bal', label: 'Alna' },
  { value: 'bbj', label: 'Bjerke' },
  { value: 'bfr', label: 'Frogner' },
  { value: 'bgo', label: 'Gamle Oslo' },
  { value: 'bgr', label: 'Grorud' },
  { value: 'bga', label: 'Grünerløkka' },
  { value: 'bna', label: 'Nordre Aker' },
  { value: 'bns', label: 'Nordstrand' },
  { value: 'bsa', label: 'Sagene' },
  { value: 'bsh', label: 'St. Hanshaugen' },
  { value: 'bsr', label: 'Stovner' },
  { value: 'bsn', label: 'Søndre Nordstrand' },
  { value: 'bun', label: 'Ullern' },
  { value: 'bva', label: 'Vestre Aker' },
  { value: 'bos', label: 'Østensjø' },
]

const bydelOptions = bydeler.map((b) => (
  <option key={b.value} value={b.value}>
    {b.label}
  </option>
))

// eslint-disable-next-line no-console -- dev route
const log = (label: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
  // eslint-disable-next-line no-console -- dev route
  console.log(label, e.target.value)

type RHFFormData = {
  firstValue: string
  secondValue: string[]
  thirdValue: string
}

export default function Combobox() {
  // State for programmatic control demos
  const [dynamicValue, setDynamicValue] = useState('')
  const [multiDynamicValue, setMultiDynamicValue] = useState('')
  const [displayAsValue, setDisplayAsValue] = useState('bga')
  const [eventLog, setEventLog] = useState<string[]>([])
  const [debugTypeahead, setDebugTypeahead] = useState('')

  // State for dynamic options demo
  const [dynamicOptions, setDynamicOptions] = useState<IPktComboboxOption[]>([
    { value: 'js', label: 'JavaScript' },
    { value: 'ts', label: 'TypeScript' },
    { value: 'py', label: 'Python' },
  ])

  // State for dynamic children demo
  const [slotOptions, setSlotOptions] = useState(['option 1', 'option 2', 'option 3'])

  const addToLog = (msg: string) => {
    setEventLog((prev) => [...prev.slice(-9), msg])
  }

  // React Hook Form
  const { handleSubmit, register, getValues } = useForm<RHFFormData>({
    defaultValues: {
      firstValue: 'bva',
      secondValue: ['bsh', 'bva'],
      thirdValue: 'bga',
    },
  })

  const RHF_SUBMIT = useMemo(() => {
    return () => {
      const values = getValues()
      // eslint-disable-next-line no-alert -- dev route
      alert('React Hook Form submit: ' + JSON.stringify(values))
    }
  }, [getValues])

  return (
    <>
      <main className="page-main pkt-container">
        <h1>Combobox</h1>

        {/* ── 1. Single select — standard dropdown ── */}
        <section className="component">
          <h2>1. Single select — standard dropdown</h2>
          <p>Grunnleggende enkeltvalg med dropdown-liste. Ingen typeahead, ingen søk.</p>
          <div>
            <PktCombobox
              id="single-standard"
              label="Velg bydel"
              placeholder="Velg bydel"
              helptext="Velg bydelen du bor i"
              onChange={log('single-standard')}
              onValueChange={(v) =>
                // eslint-disable-next-line no-console -- dev route
                console.log('single-standard value-change', v)
              }
            >
              {bydelOptions}
            </PktCombobox>
          </div>
        </section>

        {/* ── 2. Single select — with preselected value ── */}
        <section className="component">
          <h2>2. Single select — with preselected value</h2>
          <p>defaultValue=&quot;bgr&quot; satt. Bør vise &quot;Grorud&quot; ved lasting.</p>
          <div>
            <PktCombobox
              id="single-preselected"
              label="Velg bydel"
              placeholder="Velg bydel"
              defaultValue="bgr"
              onChange={log('single-preselected')}
            >
              {bydelOptions}
            </PktCombobox>
          </div>
        </section>

        {/* ── 3. Single select — typeahead ── */}
        <section className="component">
          <h2>3. Single select — typeahead</h2>
          <p>Typeahead filtrerer listen mens du skriver. Valgt verdi vises i input-feltet.</p>
          <div>
            <PktCombobox
              id="single-typeahead"
              label="Søk etter bydel"
              placeholder="Skriv for å søke"
              typeahead
              options={bydeler.map((b) =>
                b.value === 'bal' ? { ...b, description: 'Alna ligger i østre del av Oslo' } : b,
              )}
              onChange={(e) => {
                log('single-typeahead')(e)
                setDebugTypeahead(e.target.value)
              }}
            />
            <pre style={{ marginTop: 8, padding: 8, background: '#f0f0f0', fontSize: 14 }}>value: "{debugTypeahead}"</pre>
          </div>
        </section>

        {/* ── 4. Single select — with search in dropdown ── */}
        <section className="component">
          <h2>4. Single select — with search in dropdown</h2>
          <p>includeSearch gir et søkefelt inne i dropdown-listen.</p>
          <div>
            <PktCombobox
              id="single-includeSearch"
              label="Velg bydel"
              placeholder="Velg bydel"
              includeSearch
              searchPlaceholder="Søk i listen..."
              onChange={log('single-includeSearch')}
            >
              {bydelOptions}
            </PktCombobox>
          </div>
        </section>

        {/* ── 5. Single select — allow user input (custom values) ── */}
        <section className="component">
          <h2>5. Single select — allow user input (custom values)</h2>
          <p>Brukeren kan skrive inn verdier som ikke finnes i listen.</p>
          <div>
            <PktCombobox
              id="single-userInput"
              label="Velg eller skriv inn bil"
              placeholder="Søk etter bil"
              allowUserInput
              fullwidth
              options={[
                { value: 'hciv', label: 'Honda Civic' },
                { value: 'tmsla', label: 'Tesla Model S', prefix: '🤢' },
                { value: 'f150', label: 'Ford F-150' },
                { value: 'm3', label: 'BMW M3' },
                { value: 'sierra', label: 'Ford Sierra' },
              ]}
              onChange={log('single-userInput')}
            />
          </div>
        </section>

        {/* ── 6. Single select — display value as label ── */}
        <section className="component">
          <h2>6. Single select — display value as label</h2>
          <p>
            Hvert valg har en value (f.eks. &quot;bga&quot;) og en label (f.eks. &quot;Grünerløkka&quot;). Med
            displayValueAs=&quot;label&quot; viser comboboxen labelen i inputfeltet i stedet for verdien.
          </p>
          <div>
            <PktCombobox
              id="display-label"
              label="Velg bydel"
              placeholder="Velg bydel"
              displayValueAs="label"
              value={displayAsValue}
              onChange={(e) => setDisplayAsValue(e.target.value)}
              options={bydeler}
            />
          </div>
          <pre
            style={{
              background: 'var(--pkt-color-grey-1, #f5f5f5)',
              padding: '1rem',
              borderRadius: '4px',
              fontSize: '0.85rem',
            }}
          >
            {`value: ${displayAsValue || '(ingen)'}
label: ${bydeler.find((b) => b.value === displayAsValue)?.label || '(ingen)'}`}
          </pre>
        </section>

        {/* ── 7. Single select — display value as prefixAndValue ── */}
        <section className="component">
          <h2>7. Single select — display value as prefixAndValue</h2>
          <p>Landskoder med flagg-prefiks. displayValueAs=&quot;prefixAndValue&quot;.</p>
          <div>
            <PktCombobox
              id="countrycode-single"
              displayValueAs="prefixAndValue"
              options={countrycodes as IPktComboboxOption[]}
              name="countrycode"
              includeSearch
              searchPlaceholder="Søk etter land..."
              defaultValue="+47"
              onChange={log('single-prefixAndValue')}
            />
          </div>
        </section>

        {/* ── 8. Multiple select — typeahead ── */}
        <section className="component">
          <h2>8. Multiple select — typeahead</h2>
          <p>Flervalg med typeahead. Valgte verdier vises som tags inne i inputfeltet.</p>
          <div>
            <PktCombobox
              id="multi-typeahead"
              label="Velg bydeler"
              placeholder="Skriv for å søke"
              helptext="Velg en eller flere bydeler"
              multiple
              typeahead
              onChange={log('multi-typeahead')}
            >
              {bydelOptions}
            </PktCombobox>
          </div>
        </section>

        {/* ── 9. Multiple select — tags outside ── */}
        <section className="component">
          <h2>9. Multiple select — tags outside</h2>
          <p>tagPlacement=&quot;outside&quot; viser valgte verdier som tags under inputfeltet.</p>
          <div>
            <PktCombobox
              id="multi-tagsOutside"
              label="Velg bydeler"
              placeholder="Velg bydeler"
              helptext="Tags vises under inputfeltet"
              multiple
              tagPlacement="outside"
              onChange={log('multi-tagsOutside')}
            >
              {bydelOptions}
            </PktCombobox>
          </div>
        </section>

        {/* ── 10. Multiple select — preselected values ── */}
        <section className="component">
          <h2>10. Multiple select — preselected values</h2>
          <p>Flervalg med preselected values via defaultValue-array. Tags utenfor.</p>
          <PktCombobox
            id="multi-preselected"
            label="Favorittbydeler"
            placeholder="Velg bydeler"
            multiple
            tagPlacement="outside"
            defaultValue={['bfr', 'bga', 'bsa']}
            onChange={log('multi-preselected')}
          >
            {bydelOptions}
          </PktCombobox>
        </section>

        {/* ── 11. Multiple select — preselected via options.selected ── */}
        <section className="component">
          <h2>11. Multiple select — preselected via options.selected</h2>
          <p>selected: true i options-objektet. Bør vise INGEN og HODEPINE som valgt.</p>
          <PktCombobox
            id="multi-preselectedOptions"
            label="Bivirkninger"
            multiple
            typeahead
            options={[
              { value: 'HODEPINE', selected: true },
              { value: 'KVALME' },
              { value: 'INGEN', selected: true },
              { value: 'SVIMMELHET' },
            ]}
            onChange={log('multi-preselectedOptions')}
          />
        </section>

        {/* ── 12. Multiple select — maxlength ── */}
        <section className="component">
          <h2>12. Multiple select — maxlength</h2>
          <p>maxlength=3 begrenser antall valg til 3.</p>
          <div>
            <PktCombobox
              id="multi-maxlength"
              label="Velg maks 3 bydeler"
              placeholder="Velg bydeler"
              helptext="Du kan velge inntil 3 bydeler"
              multiple
              maxlength={3}
              includeSearch
              searchPlaceholder="Søk..."
              onChange={log('multi-maxlength')}
            >
              {bydelOptions}
            </PktCombobox>
          </div>
        </section>

        {/* ── 13. Multiple select — allow user input ── */}
        <section className="component">
          <h2>13. Multiple select — allow user input</h2>
          <p>Flervalg med egendefinerte verdier og fargede tags.</p>
          <div>
            <PktCombobox
              id="multi-userInput"
              label="Velg eller legg til biler"
              placeholder="Søk etter bil"
              allowUserInput
              multiple
              maxlength={5}
              searchPlaceholder="Søk etter bil"
              options={[
                { value: 'hciv', label: 'Honda Civic', tagSkinColor: 'green' },
                { value: 'tmsla', label: 'Tesla Model S', prefix: '🤢', tagSkinColor: 'yellow' },
                { value: 'f150', label: 'Ford F-150', tagSkinColor: 'blue' },
                { value: 'm3', label: 'BMW M3', tagSkinColor: 'red' },
                { value: 'sierra', label: 'Ford Sierra' },
              ]}
              onChange={log('multi-userInput')}
            />
          </div>
        </section>

        {/* ── 14. Multiple select — search in dropdown with descriptions ── */}
        <section className="component">
          <h2>14. Multiple select — search in dropdown with descriptions</h2>
          <p>Beskrivelser på hvert valg vises i listen.</p>
          <div>
            <PktCombobox
              id="multi-descriptions"
              label="Velg biler"
              multiple
              maxlength={4}
              includeSearch
              searchPlaceholder="Søk etter bil"
              options={[
                {
                  value: 'hciv',
                  label: 'Honda Civic',
                  description: 'Japansk kompaktbil, svært populær',
                },
                {
                  value: 'tmsla',
                  label: 'Tesla Model S',
                  prefix: '🤢',
                  description: 'Elektrisk sedan fra Tesla',
                },
                {
                  value: 'f150',
                  label: 'Ford F-150',
                  description: 'Amerikansk pickup-truck',
                },
                { value: 'm3', label: 'BMW M3', description: 'Tysk sportsbil fra BMW' },
                {
                  value: 'sierra',
                  label: 'Ford Sierra',
                  description: 'Klassisk Ford fra 80-tallet',
                },
              ]}
              onChange={log('multi-descriptions')}
            />
          </div>
        </section>

        {/* ── 15. Multiple select — all values preselected ── */}
        <section className="component">
          <h2>15. Multiple select — all values preselected</h2>
          <p>Alle bydeler er forhåndsvalgt. Tags utenfor. Test fjerning og re-valg.</p>
          <PktCombobox
            id="multi-allPreselected"
            label="Favorittbydeler"
            placeholder="Velg bydeler"
            multiple
            defaultValue={bydeler.map((b) => b.value)}
            onChange={log('multi-allPreselected')}
          >
            {bydelOptions}
          </PktCombobox>
        </section>

        {/* ── 16. Multiple select — prefixAndValue with tags outside ── */}
        <section className="component">
          <h2>16. Multiple select — prefixAndValue with tags outside</h2>
          <p>Landskoder med flervalg og tags utenfor.</p>
          <PktCombobox
            id="multi-countrycodes"
            displayValueAs="prefixAndValue"
            options={countrycodes as IPktComboboxOption[]}
            includeSearch
            searchPlaceholder="Søk etter land..."
            multiple
            tagPlacement="outside"
            defaultValue={['+47', '+46']}
            onChange={log('multi-countrycodes')}
          />
        </section>

        {/* ── 17. Disabled ── */}
        <section className="component">
          <h2>17. Disabled</h2>
          <div className="pkt-grid pkt-grid--full">
            <div className="pkt-cell pkt-cell--span6">
              <h3>Single, disabled</h3>
              <PktCombobox id="disabled-single" label="Velg bydel" placeholder="Velg bydel" defaultValue="bgr" disabled>
                {bydelOptions}
              </PktCombobox>
            </div>
            <div className="pkt-cell pkt-cell--span6">
              <h3>Multiple, disabled, with values</h3>
              <PktCombobox
                id="disabled-multi"
                displayValueAs="prefixAndValue"
                options={countrycodes as IPktComboboxOption[]}
                includeSearch
                disabled
                multiple
                tagPlacement="outside"
                defaultValue={['+47', '+355']}
                onChange={log('disabled-multi')}
              />
            </div>
          </div>
        </section>

        {/* ── 18. Error state ── */}
        <section className="component">
          <h2>18. Error state</h2>
          <div className="pkt-grid pkt-grid--full">
            <div className="pkt-cell pkt-cell--span6">
              <h3>Med feilmelding</h3>
              <PktCombobox
                id="error-single"
                label="Velg bydel"
                placeholder="Velg bydel"
                hasError
                errorMessage="Du må velge en bydel"
                includeSearch
                onChange={log('error-state')}
              >
                {bydelOptions}
              </PktCombobox>
            </div>
            <div className="pkt-cell pkt-cell--span6">
              <h3>Multiple med feilmelding</h3>
              <PktCombobox
                id="error-multi"
                label="Velg bydeler"
                placeholder="Velg bydeler"
                hasError
                errorMessage="Du må velge minst 2 bydeler"
                multiple
                tagPlacement="outside"
                onChange={log('error-multi')}
              >
                {bydelOptions}
              </PktCombobox>
            </div>
          </div>
        </section>

        {/* ── 19. Helptext variants ── */}
        <section className="component">
          <h2>19. Helptext variants</h2>
          <div className="pkt-grid pkt-grid--full">
            <div className="pkt-cell pkt-cell--span4">
              <PktCombobox
                id="helptext-attr"
                label="Med helptext-prop"
                helptext="Enkel hjelpetekst via prop"
                placeholder="Velg bydel"
                onChange={log('helptext-attr')}
              >
                <option value="bfr">Frogner</option>
                <option value="bgo">Gamle Oslo</option>
                <option value="bgr">Grorud</option>
              </PktCombobox>
            </div>
            <div className="pkt-cell pkt-cell--span4">
              <PktCombobox
                id="helptext-jsx"
                label="Med JSX helptext"
                helptext={
                  <>
                    Hjelpetekst med <strong>HTML-formatering</strong>
                  </>
                }
                placeholder="Velg bydel"
                onChange={log('helptext-jsx')}
              >
                <option value="bfr">Frogner</option>
                <option value="bgo">Gamle Oslo</option>
                <option value="bgr">Grorud</option>
              </PktCombobox>
            </div>
            <div className="pkt-cell pkt-cell--span4">
              <PktCombobox
                id="helptext-dropdown"
                label="Med dropdown-helptext"
                helptext="Kort hjelpetekst"
                helptextDropdown="Utvidet hjelpetekst som vises i en dropdown"
                helptextDropdownButton="Vis mer info"
                placeholder="Velg bydel"
                onChange={log('helptext-dropdown')}
              >
                <option value="bfr">Frogner</option>
                <option value="bgo">Gamle Oslo</option>
                <option value="bgr">Grorud</option>
              </PktCombobox>
            </div>
          </div>
        </section>

        {/* ── 20. Required and optional tags ── */}
        <section className="component">
          <h2>20. Required and optional tags</h2>
          <div className="pkt-grid pkt-grid--full">
            <div className="pkt-cell pkt-cell--span4">
              <PktCombobox
                id="required"
                label="Påkrevd felt"
                placeholder="Velg bydel"
                required
                requiredTag
                requiredText="Påkrevd"
                onChange={log('required')}
              >
                <option value="bfr">Frogner</option>
                <option value="bgo">Gamle Oslo</option>
                <option value="bgr">Grorud</option>
              </PktCombobox>
            </div>
            <div className="pkt-cell pkt-cell--span4">
              <PktCombobox
                id="optional"
                label="Valgfritt felt"
                placeholder="Velg bydel"
                optionalTag
                optionalText="Valgfritt"
                onChange={log('optional')}
              >
                <option value="bfr">Frogner</option>
                <option value="bgo">Gamle Oslo</option>
                <option value="bgr">Grorud</option>
              </PktCombobox>
            </div>
            <div className="pkt-cell pkt-cell--span4">
              <PktCombobox
                id="tagText"
                label="Med tagText"
                placeholder="Velg bydel"
                tagText="Bydel"
                onChange={log('tagText')}
              >
                <option value="bfr">Frogner</option>
                <option value="bgo">Gamle Oslo</option>
                <option value="bgr">Grorud</option>
              </PktCombobox>
            </div>
          </div>
        </section>

        {/* ── 21. Fullwidth ── */}
        <section className="component">
          <h2>21. Fullwidth</h2>
          <PktCombobox
            id="fullwidth"
            label="Fullbredde combobox"
            placeholder="Denne comboboxen fyller hele bredden"
            fullwidth
            onChange={log('fullwidth')}
          >
            {bydelOptions}
          </PktCombobox>
        </section>

        {/* ── 22. Options via options prop (not children) ── */}
        <section className="component">
          <h2>22. Options via options prop (not children)</h2>
          <p>Valgmuligheter sendt inn som JS-objekt via options-proppen.</p>
          <div>
            <PktCombobox
              id="options-prop"
              label="Velg bydel"
              placeholder="Velg bydel"
              options={bydeler}
              includeSearch
              searchPlaceholder="Søk..."
              onChange={log('options-prop')}
            />
          </div>
        </section>

        {/* ── 23. Options via defaultOptions prop ── */}
        <section className="component">
          <h2>23. Options via defaultOptions prop</h2>
          <p>defaultOptions brukes for comboboxer der listen ikke alltid skal vises (f.eks. allowUserInput).</p>
          <div>
            <PktCombobox
              id="defaultOptions"
              label="Velg eller skriv inn bil"
              placeholder="Søk etter bil"
              allowUserInput
              defaultOptions={[
                { value: 'hciv', label: 'Honda Civic' },
                { value: 'tmsla', label: 'Tesla Model S', prefix: '🤢' },
                { value: 'f150', label: 'Ford F-150' },
                { value: 'm3', label: 'BMW M3' },
                { value: 'sierra', label: 'Ford Sierra' },
              ]}
              onChange={log('defaultOptions')}
            />
          </div>
        </section>

        {/* ── 24. Only two options ── */}
        <section className="component">
          <h2>24. Only two options</h2>
          <p>Minimalt antall valg.</p>
          <PktCombobox id="two-options" label="Ja eller nei" placeholder="Velg" onChange={log('two-options')}>
            <option value="ja">Ja</option>
            <option value="nei">Nei</option>
          </PktCombobox>
        </section>

        {/* ── 25. Large option set (landskoder) ── */}
        <section className="component">
          <h2>25. Large option set (landskoder)</h2>
          <p>Ca. 200 alternativer med prefiks-flagg. Test ytelse med store lister.</p>
          <div>
            <PktCombobox
              id="large-set"
              label="Velg landskode"
              displayValueAs="prefixAndValue"
              options={countrycodes as IPktComboboxOption[]}
              includeSearch
              searchPlaceholder="Søk etter land eller kode..."
              onChange={log('large-set')}
            />
          </div>
        </section>

        {/* ── 26. Tag skin colors ── */}
        <section className="component">
          <h2>26. Tag skin colors</h2>
          <p>Flervalg med ulike tag-farger.</p>
          <PktCombobox
            id="tagColors"
            label="Funksjoner"
            multiple
            tagPlacement="outside"
            displayValueAs="prefixAndValue"
            options={[
              {
                value: 'Enkeltvalg',
                prefix: '🕺',
                tagSkinColor: 'blue',
                description: 'For enkeltvalg',
              },
              {
                value: 'Flervalg',
                prefix: '👯',
                tagSkinColor: 'green',
                description: 'For flervalg',
              },
              {
                value: 'Typeahead',
                prefix: '📝',
                tagSkinColor: 'red',
                description: 'Filtrering mens du skriver',
              },
              {
                value: 'Søk',
                prefix: '🔍',
                tagSkinColor: 'yellow',
                description: 'Søk i listen',
              },
              {
                value: 'Brukerinput',
                prefix: '💡',
                tagSkinColor: 'blue-dark',
                description: 'Egendefinerte verdier',
              },
              {
                value: 'Tag-plassering',
                prefix: '💪',
                tagSkinColor: 'beige',
                description: 'Tags inni eller utenfor',
              },
              {
                value: 'Maks-grense',
                prefix: '🤷',
                tagSkinColor: 'grey',
                description: 'Begrens valg',
              },
            ]}
            onChange={log('tagColors')}
          />
        </section>

        {/* ── 27. Dynamic options — add/remove/replace ── */}
        <section className="component">
          <h2>27. Dynamic options — add/remove/replace</h2>
          <p>Test dynamisk oppdatering av valgmuligheter.</p>
          <div>
            <PktCombobox
              id="dynamic-options"
              label="Programmeringsspråk"
              placeholder="Velg språk"
              options={dynamicOptions}
              includeSearch
              onChange={log('dynamic-options')}
            />
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
              <PktButton
                onClick={() => {
                  const langs = [
                    { value: 'go', label: 'Go' },
                    { value: 'rust', label: 'Rust' },
                    { value: 'ruby', label: 'Ruby' },
                    { value: 'swift', label: 'Swift' },
                    { value: 'kotlin', label: 'Kotlin' },
                  ]
                  const next = langs.find((l) => !dynamicOptions.some((o) => o.value === l.value))
                  if (next) setDynamicOptions([...dynamicOptions, next])
                }}
              >
                Legg til
              </PktButton>
              <PktButton
                skin="secondary"
                onClick={() => {
                  if (dynamicOptions.length > 1) setDynamicOptions(dynamicOptions.slice(0, -1))
                }}
              >
                Fjern siste
              </PktButton>
              <PktButton
                skin="secondary"
                onClick={() =>
                  setDynamicOptions([
                    { value: 'java', label: 'Java' },
                    { value: 'csharp', label: 'C#' },
                    { value: 'php', label: 'PHP' },
                  ])
                }
              >
                Erstatt alle
              </PktButton>
              <PktButton
                skin="secondary"
                onClick={() =>
                  setDynamicOptions([
                    { value: 'js', label: 'JavaScript' },
                    { value: 'ts', label: 'TypeScript' },
                    { value: 'py', label: 'Python' },
                  ])
                }
              >
                Tilbakestill
              </PktButton>
            </div>
          </div>
        </section>

        {/* ── 28. Programmatic value control — single ── */}
        <section className="component">
          <h2>28. Programmatic value control — single</h2>
          <p>Verdi styrt eksternt. Klikk knappene for å endre verdien.</p>
          <PktCombobox
            id="programmatic-single"
            label="Ekstern verdikontroll"
            placeholder="Velg bydel"
            value={dynamicValue}
            onChange={(e) => {
              setDynamicValue(e.target.value)
              // eslint-disable-next-line no-console -- dev route
              console.log('programmatic-single', e.target.value)
            }}
          >
            {bydelOptions}
          </PktCombobox>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
            <PktButton onClick={() => setDynamicValue('bga')}>Sett Grünerløkka</PktButton>
            <PktButton onClick={() => setDynamicValue('bns')}>Sett Nordstrand</PktButton>
            <PktButton skin="secondary" onClick={() => setDynamicValue('')}>
              Nullstill
            </PktButton>
          </div>
          <p style={{ marginTop: '0.5rem' }}>
            Nåværende verdi: <code>{dynamicValue || '(tom)'}</code>
          </p>
        </section>

        {/* ── 29. Programmatic value control — multiple ── */}
        <section className="component">
          <h2>29. Programmatic value control — multiple</h2>
          <p>Flervalg styrt eksternt med kommaseparerte verdier.</p>
          <PktCombobox
            id="programmatic-multi"
            label="Ekstern verdikontroll (flervalg)"
            placeholder="Velg bydeler"
            multiple
            tagPlacement="outside"
            value={multiDynamicValue}
            onChange={(e) => {
              setMultiDynamicValue(e.target.value)
              // eslint-disable-next-line no-console -- dev route
              console.log('programmatic-multi', e.target.value)
            }}
          >
            {bydelOptions}
          </PktCombobox>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
            <PktButton onClick={() => setMultiDynamicValue('bfr,bga,bsa')}>Sett 3 bydeler</PktButton>
            <PktButton onClick={() => setMultiDynamicValue('bal,bbj,bfr,bgo,bgr')}>Sett 5 bydeler</PktButton>
            <PktButton skin="secondary" onClick={() => setMultiDynamicValue('')}>
              Nullstill
            </PktButton>
          </div>
          <p style={{ marginTop: '0.5rem' }}>
            Nåværende verdi: <code>{multiDynamicValue || '(tom)'}</code>
          </p>
        </section>

        {/* ── 30. Event monitoring ── */}
        <section className="component">
          <h2>30. Event monitoring</h2>
          <p>Logg av onChange- og onValueChange-events. Sjekk konsollen og loggen under.</p>
          <div>
            <PktCombobox
              id="event-monitor"
              label="Velg noe (se event-logg)"
              placeholder="Velg bydel"
              multiple
              typeahead
              onChange={(e) => {
                addToLog(`onChange: ${e.target.value}`)
                // eslint-disable-next-line no-console -- dev route
                console.log('event-monitor onChange:', e.target.value)
              }}
              onValueChange={(v) => {
                addToLog(`onValueChange: ${JSON.stringify(v)}`)
                // eslint-disable-next-line no-console -- dev route
                console.log('event-monitor onValueChange:', v)
              }}
            >
              {bydelOptions}
            </PktCombobox>
          </div>
          <div
            style={{
              background: 'var(--pkt-color-grey-1, #f5f5f5)',
              padding: '1rem',
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              maxHeight: '200px',
              overflowY: 'auto',
            }}
          >
            <strong>Event-logg:</strong>
            <br />
            {eventLog.length === 0 ? (
              <em>Ingen hendelser ennå...</em>
            ) : (
              eventLog.map((msg, i) => (
                <span key={i}>
                  {msg}
                  <br />
                </span>
              ))
            )}
          </div>
        </section>

        {/* ── 31. Side-by-side comparison ── */}
        <section className="component">
          <h2>31. Side-by-side comparison</h2>
          <p>Sammenlign ulike moduser side om side.</p>
          <div className="pkt-grid pkt-grid--full">
            <div className="pkt-cell pkt-cell--span4">
              <PktCombobox
                id="compare-standard"
                label="Standard dropdown"
                placeholder="Velg bydel"
                onChange={log('compare-standard')}
              >
                <option value="bfr">Frogner</option>
                <option value="bgo">Gamle Oslo</option>
                <option value="bgr">Grorud</option>
                <option value="bga">Grünerløkka</option>
                <option value="bna">Nordre Aker</option>
              </PktCombobox>
            </div>
            <div className="pkt-cell pkt-cell--span4">
              <PktCombobox
                id="compare-typeahead"
                label="Med typeahead"
                placeholder="Skriv for å søke"
                typeahead
                onChange={log('compare-typeahead')}
              >
                <option value="bfr">Frogner</option>
                <option value="bgo">Gamle Oslo</option>
                <option value="bgr">Grorud</option>
                <option value="bga">Grünerløkka</option>
                <option value="bna">Nordre Aker</option>
              </PktCombobox>
            </div>
            <div className="pkt-cell pkt-cell--span4">
              <PktCombobox
                id="compare-search"
                label="Med søk i dropdown"
                placeholder="Velg bydel"
                includeSearch
                searchPlaceholder="Søk..."
                onChange={log('compare-search')}
              >
                <option value="bfr">Frogner</option>
                <option value="bgo">Gamle Oslo</option>
                <option value="bgr">Grorud</option>
                <option value="bga">Grünerløkka</option>
                <option value="bna">Nordre Aker</option>
              </PktCombobox>
            </div>
          </div>
        </section>

        {/* ── 32. useWrapper=false ── */}
        <section className="component">
          <h2>32. useWrapper=false</h2>
          <p>Combobox uten input-wrapper. Nyttig for egendefinert layout.</p>
          <PktCombobox
            id="no-wrapper"
            label="Uten wrapper"
            defaultValue="bgr"
            allowUserInput
            helptext="Denne har useWrapper=false"
            useWrapper={false}
            onChange={log('no-wrapper')}
          >
            {bydelOptions}
          </PktCombobox>
        </section>

        {/* ── 33. Form integration — plain HTML form ── */}
        <section className="component">
          <h2>33. Form integration — plain HTML form</h2>
          <p>Combobox i et vanlig skjema uten bibliotek. Test onChange og FormData.</p>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              const formData = new FormData(e.target as HTMLFormElement)
              const data: Record<string, string> = {}
              for (const [key, value] of formData.entries()) {
                data[key] = value as string
              }
              // eslint-disable-next-line no-console, no-alert -- dev route
              console.log('Plain form submitted:', data)
              // eslint-disable-next-line no-alert -- dev route
              alert(`Skjema sendt!\n\n${JSON.stringify(data, null, 2)}`)
            }}
          >
            <PktCombobox
              id="form-bydel"
              label="Bydel"
              name="bydel"
              placeholder="Velg din bydel"
              required
              requiredTag
              requiredText="Påkrevd"
              helptext="Påkrevd felt — skjemaet kan ikke sendes uten"
              typeahead
              onChange={log('form-bydel')}
            >
              {bydelOptions}
            </PktCombobox>

            <PktCombobox
              id="form-skills"
              label="Kompetanser"
              name="skills"
              placeholder="Velg eller skriv inn"
              multiple
              allowUserInput
              tagPlacement="outside"
              optionalTag
              optionalText="Valgfritt"
              helptext="Skriv inn kompetanser eller velg fra listen"
              options={[
                { value: 'frontend', label: 'Frontend' },
                { value: 'backend', label: 'Backend' },
                { value: 'devops', label: 'DevOps' },
                { value: 'design', label: 'Design' },
                { value: 'testing', label: 'Testing' },
              ]}
              onChange={log('form-skills')}
            />

            <PktCombobox
              id="form-countrycode"
              label="Landskode"
              name="countrycode"
              displayValueAs="prefixAndValue"
              options={countrycodes as IPktComboboxOption[]}
              includeSearch
              searchPlaceholder="Søk etter land..."
              defaultValue="+47"
              required
              requiredTag
              requiredText="Påkrevd"
              onChange={log('form-countrycode')}
            />

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              <PktButton type="submit">Send skjema</PktButton>
              <PktButton type="reset" skin="secondary">
                Nullstill
              </PktButton>
            </div>
          </form>
        </section>

        {/* ── 34. Form integration — reset behavior ── */}
        <section className="component">
          <h2>34. Form integration — reset behavior</h2>
          <p>Test at reset-knappen nullstiller alle verdier korrekt.</p>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              const formData = new FormData(e.target as HTMLFormElement)
              // eslint-disable-next-line no-console -- dev route
              console.log('Reset-form submitted:', Object.fromEntries(formData.entries()))
            }}
            onReset={() => {
              // eslint-disable-next-line no-console -- dev route
              console.log('Form reset triggered')
            }}
          >
            <div className="pkt-grid pkt-grid--full">
              <div className="pkt-cell pkt-cell--span6">
                <PktCombobox id="singleReset" label="Single med verdi" name="singleReset" defaultValue="bga" typeahead>
                  {bydelOptions}
                </PktCombobox>
              </div>
              <div className="pkt-cell pkt-cell--span6">
                <PktCombobox
                  id="multiReset"
                  label="Multiple med verdier"
                  name="multiReset"
                  defaultValue={['bfr', 'bgo', 'bgr']}
                  multiple
                  tagPlacement="outside"
                >
                  {bydelOptions}
                </PktCombobox>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              <PktButton type="reset" skin="secondary">
                Nullstill skjema
              </PktButton>
              <PktButton type="submit">Send</PktButton>
            </div>
          </form>
        </section>

        {/* ── 35. Edge case: Empty options list ── */}
        <section className="component">
          <h2>35. Edge case: Empty options list</h2>
          <p>Combobox uten noen valgmuligheter.</p>
          <PktCombobox
            id="empty-options"
            label="Ingen alternativer"
            placeholder="Det finnes ingen alternativer"
            onChange={log('empty-options')}
          />
        </section>

        {/* ── 36. Edge case: Very long labels ── */}
        <section className="component">
          <h2>36. Edge case: Very long labels</h2>
          <p>Test med veldig lange tekster i labels og verdier.</p>
          <PktCombobox
            id="long-labels"
            label="Velg en setning"
            placeholder="Velg en veldig lang setning"
            onChange={log('long-labels')}
          >
            <option value="a">
              Dette er en veldig lang tekst som skal teste hvordan comboboxen håndterer overflow og tekstbryting i
              dropdown-listen
            </option>
            <option value="b">
              En annen lang setning for å teste at alt ser bra ut med mye tekst i valgmuligheten
            </option>
            <option value="c">Kort</option>
          </PktCombobox>
        </section>

        {/* ── 37. Edge case: Special characters ── */}
        <section className="component">
          <h2>37. Edge case: Special characters</h2>
          <p>Test med spesialtegn i verdier og labels.</p>
          <PktCombobox
            id="special-chars"
            label="Spesialtegn"
            placeholder="Velg"
            allowUserInput
            onChange={log('special-chars')}
          >
            <option value="æøå">Æøå — Norske tegn</option>
            <option value="html<>">{'HTML <tags> i verdier'}</option>
            <option value='quotes"'>{'Anførselstegn "test"'}</option>
            <option value="comma,value">Komma i verdi</option>
            <option value="emoji">{'🎉 Emojis 🚀 i label'}</option>
          </PktCombobox>
        </section>

        {/* ── 38. Keyboard navigation test ── */}
        <section className="component">
          <h2>38. Keyboard navigation test</h2>
          <p>
            Test tastaturnavigasjon: Tab for å gå inn, ArrowDown for å åpne/navigere, Enter for å velge, Escape for å
            lukke, Shift+Tab for å gå ut.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <input type="text" placeholder="Tab-stopp før combobox" style={{ padding: '0.5rem' }} />
            <PktCombobox
              id="keyboard-nav"
              label="Tastaturnavigasjon"
              placeholder="Bruk tastaturet"
              typeahead
              onChange={log('keyboard-nav')}
            >
              {bydelOptions}
            </PktCombobox>
            <input type="text" placeholder="Tab-stopp etter combobox" style={{ padding: '0.5rem' }} />
          </div>
        </section>

        {/* ── 39. Dynamic children options — add/remove ── */}
        <section className="component">
          <h2>39. Dynamic children options — add/remove &lt;option&gt; children</h2>
          <p>Test at comboboxen oppdaterer seg når &lt;option&gt;-elementer legges til eller fjernes dynamisk.</p>
          <div>
            <PktCombobox
              id="dynamic-slot"
              label="Dynamiske children-options"
              placeholder="Velg"
              onChange={log('dynamic-slot')}
            >
              {slotOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </PktCombobox>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
            <PktButton onClick={() => setSlotOptions([...slotOptions, 'option ' + (slotOptions.length + 1)])}>
              Legg til
            </PktButton>
            <PktButton
              skin="secondary"
              onClick={() => {
                if (slotOptions.length > 0) setSlotOptions(slotOptions.slice(0, -1))
              }}
            >
              Fjern siste
            </PktButton>
            <PktButton skin="secondary" onClick={() => setSlotOptions([])}>
              Tøm alle
            </PktButton>
            <PktButton skin="secondary" onClick={() => setSlotOptions(['option 1', 'option 2', 'option 3'])}>
              Tilbakestill
            </PktButton>
          </div>
        </section>

        {/* ── 40. Multiple comboboxes in grid ── */}
        <section className="component">
          <h2>40. Multiple comboboxes in grid</h2>
          <p>Flere comboboxer i et grid-oppsett — test at dropdown ikke kolliderer.</p>
          <div className="pkt-grid pkt-grid--full">
            <div className="pkt-cell pkt-cell--span3">
              <PktCombobox id="grid-1" label="Bydel 1" placeholder="Velg" onChange={log('grid-1')}>
                <option value="bfr">Frogner</option>
                <option value="bgo">Gamle Oslo</option>
                <option value="bgr">Grorud</option>
                <option value="bga">Grünerløkka</option>
              </PktCombobox>
            </div>
            <div className="pkt-cell pkt-cell--span3">
              <PktCombobox id="grid-2" label="Bydel 2" placeholder="Velg" typeahead onChange={log('grid-2')}>
                <option value="bna">Nordre Aker</option>
                <option value="bns">Nordstrand</option>
                <option value="bsa">Sagene</option>
                <option value="bsh">St. Hanshaugen</option>
              </PktCombobox>
            </div>
            <div className="pkt-cell pkt-cell--span3">
              <PktCombobox id="grid-3" label="Bydel 3" placeholder="Velg" includeSearch onChange={log('grid-3')}>
                <option value="bsr">Stovner</option>
                <option value="bsn">Søndre Nordstrand</option>
                <option value="bun">Ullern</option>
                <option value="bva">Vestre Aker</option>
              </PktCombobox>
            </div>
            <div className="pkt-cell pkt-cell--span3">
              <PktCombobox id="grid-4" label="Bydel 4" placeholder="Velg" multiple onChange={log('grid-4')}>
                <option value="bos">Østensjø</option>
                <option value="bal">Alna</option>
                <option value="bbj">Bjerke</option>
              </PktCombobox>
            </div>
          </div>
        </section>

        {/* ── 41. React Hook Form integration ── */}
        <section className="component">
          <h2>41. React Hook Form integration</h2>
          <p>Combobox med react-hook-form register(). Test default values og submit.</p>
          <form onSubmit={handleSubmit(RHF_SUBMIT)}>
            <PktCombobox
              id="rhf-firstValue"
              label="First value (allowUserInput)"
              allowUserInput
              {...register('firstValue')}
            >
              <option value="bfr">Frogner</option>
              <option value="bgo">Gamle Oslo</option>
              <option value="bgr">Grorud</option>
              <option value="bga">Grünerløkka</option>
              <option value="bna">Nordre Aker</option>
              <option value="bns">Nordstrand</option>
              <option value="bsa">Sagene</option>
              <option value="bsh">St. Hanshaugen</option>
              <option value="bsr">Stovner</option>
              <option value="bsn">Søndre Nordstrand</option>
              <option value="bun">Ullern</option>
              <option value="bva">Vestre Aker</option>
              <option value="bos">Østensjø</option>
            </PktCombobox>
            <PktCombobox
              id="rhf-secondValue"
              label="Second value (multiple, allowUserInput)"
              multiple
              allowUserInput
              {...register('secondValue')}
            >
              <option value="bfr">Frogner</option>
              <option value="bgo">Gamle Oslo</option>
              <option value="bgr">Grorud</option>
              <option value="bga">Grünerløkka</option>
              <option value="bna">Nordre Aker</option>
              <option value="bns">Nordstrand</option>
              <option value="bsa">Sagene</option>
              <option value="bsh">St. Hanshaugen</option>
              <option value="bsr">Stovner</option>
              <option value="bsn">Søndre Nordstrand</option>
              <option value="bun">Ullern</option>
              <option value="bva">Vestre Aker</option>
              <option value="bos">Østensjø</option>
            </PktCombobox>
            <PktCombobox id="rhf-thirdValue" label="Third value (typeahead)" typeahead {...register('thirdValue')}>
              <option value="bfr">Frogner</option>
              <option value="bgo">Gamle Oslo</option>
              <option value="bgr">Grorud</option>
              <option value="bga">Grünerløkka</option>
              <option value="bna">Nordre Aker</option>
              <option value="bns">Nordstrand</option>
              <option value="bsa">Sagene</option>
              <option value="bsh">St. Hanshaugen</option>
              <option value="bsr">Stovner</option>
              <option value="bsn">Søndre Nordstrand</option>
              <option value="bun">Ullern</option>
              <option value="bva">Vestre Aker</option>
              <option value="bos">Østensjø</option>
            </PktCombobox>
            <PktButton type="submit">Submit</PktButton>
          </form>
        </section>
      </main>
    </>
  )
}
